import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  NgZone
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { GoogleAudioService } from 'src/app/services/googleaudio.service';

@Component({
  selector: 'app-the-voice',
  templateUrl: './the-voice.component.html',
  styleUrls: ['./the-voice.component.scss']
})
export class TheVoiceComponent implements OnInit, OnDestroy {
  // ALI NOTE - We draw two responsive wave layers (mic + model) on <canvas>
  @ViewChild('waveCanvas', { static: true }) waveCanvas!: ElementRef<HTMLCanvasElement>;

  // Session states
  sessionActive = false;
  micMuted = false;

  // Visualizer handles
  private rafId: number | null = null;
  private resizeObserver?: ResizeObserver;

  // Realtime session handle (from service)
  private activeClose?: () => void;
  private subs: Subscription[] = [];

  // Simple pulse for the center mic icon
  iconPulse = 1;

  constructor(
    private dialogRef: MatDialogRef<TheVoiceComponent>,
    private googleAudio: GoogleAudioService,
    private ngZone: NgZone
  ) {}

  async ngOnInit(): Promise<void> {
    // Start realtime session as soon as dialog opens
    await this.startVoiceSession();

    // Track session activity state
    this.subs.push(
      this.googleAudio.sessionActive$.subscribe((a) => (this.sessionActive = a))
    );

    // Begin visualizer loop
    this.startVisualizer();

    // Keep canvas sized to dialog
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.waveCanvas.nativeElement);
    this.resize();
  }

  ngOnDestroy(): void {
    this.stopVisualizer();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    // Ensure realtime session is stopped when dialog closes
    try {
      if (this.activeClose) this.activeClose();
    } catch {}
  }

  // ALI NOTE - Voice logic has been MOVED here from ClariChat.
  private async startVoiceSession(): Promise<void> {
    try {
      const result = await this.googleAudio.startLiveSession();

      if (typeof result === 'object' && result?.close) {
        this.activeClose = result.close;
      }
    } catch (e) {
      console.error('Failed to start realtime voice:', e);
    }
  }

  toggleMic(): void {
    this.micMuted = !this.micMuted;
    // Service toggles track.enabled internally
    try {
      void this.googleAudio.getAnalyzers();
      this.googleAudio.muteMic(this.micMuted);
    } catch {}
  }

  closeDialog(): void {
    try {
      if (this.activeClose) this.activeClose();
    } catch {}
    this.dialogRef.close();
  }

  // ===================== Visualizer (parallax waves) =====================

  private startVisualizer(): void {
    // ALI NOTE - We run rAF outside Angular to keep 60fps silky-smooth
    this.ngZone.runOutsideAngular(() => {
      const loop = () => {
        this.drawFrame();
        this.rafId = requestAnimationFrame(loop);
      };
      this.rafId = requestAnimationFrame(loop);
    });
  }

  private stopVisualizer(): void {
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  private resize(): void {
    const canvas = this.waveCanvas.nativeElement;
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
  }

  private drawFrame(): void {
    const canvas = this.waveCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { audioCtx, mic, remote } = this.googleAudio.getAnalyzers();

    // background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.globalAlpha = 1;

    // Read mic & remote magnitudes
    const micLevel = this.analyserLevel(mic);
    const modelLevel = this.analyserLevel(remote);

    // Parallax wave params
    const w = canvas.width;
    const h = canvas.height;
    const mid = h * 0.55;

    // Back wave (assistant/model)
    this.drawWave(ctx, {
      width: w,
      height: h,
      baseline: mid + 8,
      amplitude: 24 + modelLevel * 54,
      wavelength: 160,
      speed: 0.9,
      alpha: 0.35
    });

    // Front wave (mic/user)
    this.drawWave(ctx, {
      width: w,
      height: h,
      baseline: mid,
      amplitude: 28 + micLevel * 64,
      wavelength: 120,
      speed: 1.4,
      alpha: 0.55
    });

    // Center mic icon pulse – responds to combined energy
    const energy = Math.min(1, (micLevel * 0.65 + modelLevel * 0.8));
    this.iconPulse = 1 + energy * 0.35;
  }

  private drawWave(
    ctx: CanvasRenderingContext2D,
    opts: {
      width: number;
      height: number;
      baseline: number;
      amplitude: number;
      wavelength: number;
      speed: number;
      alpha: number;
    }
  ) {
    const { width: w, height: h, baseline: y0, amplitude: A, wavelength: L, speed, alpha } = opts;
    const now = performance.now() * 0.001 * speed;

    ctx.save();
    ctx.globalAlpha = alpha;

    // Gradient fill for nice depth
    const grad = ctx.createLinearGradient(0, y0 - A - 20, 0, y0 + A + 20);
    grad.addColorStop(0, 'rgba(175, 134, 255, 0.9)');
    grad.addColorStop(0.5, 'rgba(255, 219, 120, 0.9)');
    grad.addColorStop(1, 'rgba(255, 255, 255, 0.9)');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(0, y0);

    // Smooth sine wave
    const step = Math.max(2, Math.floor(w / 160));
    for (let x = 0; x <= w; x += step) {
      const t = (x / L) + now;
      const y = y0 + Math.sin(t * Math.PI * 2) * A * 0.8 + Math.sin(t * Math.PI * 4) * A * 0.2;
      ctx.lineTo(x, y);
    }

    // Close shape to fill softly
    ctx.lineTo(w, h);
    ctx.lineTo(0, h);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  private analyserLevel(analyser?: AnalyserNode | null): number {
    if (!analyser) return 0;
    const arr = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(arr);
    // Take average energy across bins
    let sum = 0;
    for (let i = 0; i < arr.length; i++) sum += arr[i];
    const avg = sum / (arr.length || 1);
    return avg / 255; // normalize 0..1
  }
}
